import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import "./PreviewModal.scss";
import LeftArrowIcon from "../../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../../components/icons/RightArrowIcon";

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  images?: string[];
  initialIndex?: number;
  children?: React.ReactNode;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  open,
  onClose,
  images = [],
  initialIndex = 0,
  children,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setCurrentIndex(initialIndex);
  }, [open, initialIndex]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
      return;
    }

    const target = e.target as HTMLElement;

    if (target.tagName === "IMG") {
      const img = target as HTMLImageElement;

      const rect = img.getBoundingClientRect();
      const naturalW = img.naturalWidth;
      const naturalH = img.naturalHeight;

      if (!naturalW || !naturalH) {
        e.stopPropagation();
        return;
      }

      const containerW = rect.width;
      const containerH = rect.height;

      const imageAspect = naturalW / naturalH;
      const containerAspect = containerW / containerH;

      let renderedW: number;
      let renderedH: number;

      if (imageAspect > containerAspect) {
        renderedW = containerW;
        renderedH = containerW / imageAspect;
      } else {
        renderedH = containerH;
        renderedW = containerH * imageAspect;
      }

      const centerX = rect.left + containerW / 2;
      const centerY = rect.top + containerH / 2;

      const imgLeft = centerX - renderedW / 2;
      const imgRight = centerX + renderedW / 2;
      const imgTop = centerY - renderedH / 2;
      const imgBottom = centerY + renderedH / 2;

      const insideImage =
        e.clientX >= imgLeft &&
        e.clientX <= imgRight &&
        e.clientY >= imgTop &&
        e.clientY <= imgBottom;

      if (insideImage) {
        e.stopPropagation();
      } else {
        e.stopPropagation();
        onClose();
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      maxWidth={false}
      className="brand-view-preview-modal"
      classes={{
        root: "brand-view-preview-modal-root",
        paper: "brand-view-preview-modal-paper",
      }}
      BackdropProps={{
        className: "brand-view-preview-modal-backdrop",
      }}
    >
      <div className="brand-view-preview-modal-container">
        <IconButton
          className="brand-view-preview-close-btn"
          onClick={onClose}
          aria-label="Close preview"
        >
          <CloseIcon />
        </IconButton>

        <div
          className="brand-view-preview-modal-body"
          onClick={handleBodyClick}
        >
          {children ? (
            children
          ) : (
            <>
              {images.length > 0 && (
                <img
                  src={images[currentIndex]}
                  alt={`Preview ${currentIndex + 1}`}
                  className="brand-view-preview-image"
                />
              )}

              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="brand-view-carousel-button brand-view-left"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrev();
                    }}
                    aria-label="Previous image"
                  >
                    <LeftArrowIcon />
                  </button>
                  <button
                    type="button"
                    className="brand-view-carousel-button brand-view-right"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNext();
                    }}
                    aria-label="Next image"
                  >
                    <RightArrowIcon />
                  </button>

                  <div className="brand-view-mobile-carousel-buttons">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrev();
                      }}
                      aria-label="Previous image"
                    >
                      <LeftArrowIcon />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNext();
                      }}
                      aria-label="Next image"
                    >
                      <RightArrowIcon />
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default PreviewModal;
